/**
 * Bundle calculation utilities
 * Extracted from Order.js for better maintainability and testability
 */

/**
 * Calculate the minimum amount required for a bundle
 * @param {Object} bundle - The bundle object with optionBundleEntries
 * @returns {number} - The minimum amount required
 */
export const calculateBundleMinimumAmount = (bundle) => {
    if (!bundle?.optionBundleEntries) return 0;
    return bundle.optionBundleEntries.reduce((sum, entry) => {
        const price = entry.itemsOption?.price || 0;
        return sum + (entry.quantity * price);
    }, 0);
};

/**
 * Calculate the minimum quantity required for a bundle
 * @param {Object} bundle - The bundle object with optionBundleEntries
 * @returns {number} - The minimum quantity required
 */
export const calculateBundleMinimumQuantity = (bundle) => {
    if (!bundle?.optionBundleEntries) return 0;
    return bundle.optionBundleEntries.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
};

/**
 * Find the best matching bundle for the current order
 * @param {Array} bundleOptions - Available bundle options
 * @param {Object} order - Current order object
 * @returns {Object|null} - The matching bundle or null if none found
 */
export const findMatchingBundle = (bundleOptions, order) => {
    if (!bundleOptions || bundleOptions.length === 0 || !order || !order.fundraiserOrderItems) {
        return null;
    }

    // Calculate current order totals
    let currentItemTotal = 0;
    let currentTotalQty = 0;
    
    order.fundraiserOrderItems.forEach(item => {
        item.fundraiserOrderItemOptions.forEach(option => {
            if (option.qty > 0) {
                currentItemTotal += option.qty * option.price;
                currentTotalQty += option.qty;
            }
        });
    });

    // Sort bundles by quantity descending, then by amount descending
    // This ensures excess quantities match the highest appropriate bundle
    const sortedBundles = [...bundleOptions].sort((a, b) => {
        const aQty = calculateBundleMinimumQuantity(a);
        const bQty = calculateBundleMinimumQuantity(b);
        
        // Primary sort: quantity descending (highest quantity first)
        if (bQty !== aQty) {
            return bQty - aQty;
        }
        
        // Secondary sort: amount descending (highest amount first)
        const aAmount = calculateBundleMinimumAmount(a);
        const bAmount = calculateBundleMinimumAmount(b);
        return bAmount - aAmount;
    });

    // Check each bundle to see if current order qualifies (highest quantity/amount first)
    for (const bundle of sortedBundles) {
        // Check minimum quantity requirement
        const bundleMinQty = calculateBundleMinimumQuantity(bundle);
        if (currentTotalQty < bundleMinQty) {
            continue; // Doesn't meet minimum quantity
        }
        
        // Check minimum amount requirement
        const bundleMinAmount = calculateBundleMinimumAmount(bundle);
        if (bundleMinAmount > 0 && currentItemTotal < bundleMinAmount) {
            continue; // Doesn't meet minimum amount
        }
        
        // Check if current selection can satisfy this bundle's requirements
        let bundleSatisfied = true;
        
        for (const bundleEntry of bundle.optionBundleEntries) {
            let canSatisfyEntry = false;
            
            for (const item of order.fundraiserOrderItems) {
                for (const option of item.fundraiserOrderItemOptions) {
                    if (option.qty > 0) {
                        // Find the original bundle option to compare price
                        const originalBundleOption = order.fundraiserOrderItems
                            .flatMap(i => i.fundraiserOrderItemOptions)
                            .find(o => o.itemOptionId === bundleEntry.itemsOptionId);
                        
                        // Option qualifies if:
                        // 1. Same item type and sufficient quantity, OR
                        // 2. Same/higher price and sufficient quantity
                        if ((option.itemOptionId === bundleEntry.itemsOptionId && option.qty >= bundleEntry.quantity) ||
                            (originalBundleOption && option.price >= originalBundleOption.price && option.qty >= bundleEntry.quantity)) {
                            canSatisfyEntry = true;
                            break;
                        }
                    }
                }
                if (canSatisfyEntry) break;
            }
            
            if (!canSatisfyEntry) {
                bundleSatisfied = false;
                break;
            }
        }
        
        if (bundleSatisfied) {
            // Return first qualifying bundle (which is the highest due to sorting)

            return bundle;
        }
    }
    
    // No qualifying bundle found
    return null;
};

/**
 * Analyze an order to determine if it's a bundle, bundle+extras, or custom order
 * @param {Array} bundleOptions - Available bundle options
 * @param {Object} order - Current order object
 * @returns {Object} - Analysis result with type, bundle, extraQuantities, and actualBundleEntries
 */
export const analyzeOrderForBundleAndExtras = (bundleOptions, order) => {
    const matchingBundle = findMatchingBundle(bundleOptions, order);
    
    if (!matchingBundle) {
        return {
            type: 'custom',
            bundle: null,
            extraQuantities: new Map(),
            actualBundleEntries: []
        };
    }
    
    // Create a map of what the user actually selected
    const userSelections = new Map();
    if (order && order.fundraiserOrderItems) {
        order.fundraiserOrderItems.forEach(item => {
            item.fundraiserOrderItemOptions.forEach(option => {
                if (option.qty > 0) {
                    userSelections.set(option.itemOptionId, option.qty);
                }
            });
        });
    }
    
    // Figure out the actual bundle entries that match the user's selections
    const actualBundleEntries = [];
    const extraQuantities = new Map();
    
    // Track available quantities as we allocate them to bundle entries
    const availableQuantities = new Map(userSelections);
    
    // For each bundle entry, find the best matching user selection
    matchingBundle.optionBundleEntries.forEach(bundleEntry => {
        const originalOptionId = bundleEntry.itemsOptionId;
        const requiredQty = bundleEntry.quantity;
        
        // Check if user selected the exact original option and has available quantity
        const availableQtyForOriginal = availableQuantities.get(originalOptionId) || 0;
        
        if (availableQtyForOriginal >= requiredQty) {
            // User has the original option with sufficient available quantity
            actualBundleEntries.push({
                itemsOptionId: originalOptionId,
                quantity: requiredQty
            });
            
            // Deduct the used quantity from available quantities
            availableQuantities.set(originalOptionId, availableQtyForOriginal - requiredQty);
        } else {
            // User might have selected a different (higher-priced) option
            // Find the option they actually selected that can fulfill this bundle entry
            let bestMatch = null;
            let bestMatchAvailableQty = 0;
            
            // Get the original option to compare prices
            const originalOption = order.fundraiserOrderItems
                .flatMap(item => item.fundraiserOrderItemOptions)
                .find(option => option.itemOptionId === originalOptionId);
            
            if (originalOption) {
                const minPrice = originalOption.price;
                
                // Look for options that meet the price requirement and have sufficient available quantity
                order.fundraiserOrderItems.forEach(item => {
                    item.fundraiserOrderItemOptions.forEach(option => {
                        const availableQty = availableQuantities.get(option.itemOptionId) || 0;
                        if (option.price >= minPrice && availableQty >= requiredQty) {
                            // This option could satisfy this bundle entry
                            if (!bestMatch || option.itemOptionId === originalOptionId) {
                                // Prefer the original option, or take the first valid match
                                bestMatch = option;
                                bestMatchAvailableQty = availableQty;
                            }
                        }
                    });
                });
            }
            
            if (bestMatch) {
                actualBundleEntries.push({
                    itemsOptionId: bestMatch.itemOptionId,
                    quantity: requiredQty
                });
                
                // Deduct the used quantity from available quantities
                availableQuantities.set(bestMatch.itemOptionId, bestMatchAvailableQty - requiredQty);
            }
        }
    });
    
    // Check for any user selections that weren't accounted for in the bundle
    // Use the remaining available quantities (after bundle allocation)
    availableQuantities.forEach((remainingQty, optionId) => {
        if (remainingQty > 0) {
            extraQuantities.set(optionId, remainingQty);
        }
    });
    
    // Determine if this is purely a bundle or bundle + extras
    const hasExtras = extraQuantities.size > 0;
    
    return {
        type: hasExtras ? 'bundle_with_extras' : 'bundle_only',
        bundle: matchingBundle,
        extraQuantities: extraQuantities,
        actualBundleEntries: actualBundleEntries
    };
};

/**
 * Check if the selected bundle meets all requirements
 * @param {Object} selectedBundle - The currently selected bundle
 * @param {Object} order - Current order object
 * @returns {string} - Bundle status: 'none', 'complete', or 'incomplete'
 */
export const checkBundleStatus = (selectedBundle, order) => {
    if (!selectedBundle || !order || !order.fundraiserOrderItems) {
        return 'none';
    }

    // Calculate current totals
    let currentItemTotal = 0;
    let currentTotalQty = 0;
    
    order.fundraiserOrderItems.forEach(item => {
        item.fundraiserOrderItemOptions.forEach(option => {
            if (option.qty > 0) {
                currentItemTotal += option.qty * option.price;
                currentTotalQty += option.qty;
            }
        });
    });

    // Check bundle requirements
    const bundleMinQty = calculateBundleMinimumQuantity(selectedBundle);
    const bundleMinAmount = calculateBundleMinimumAmount(selectedBundle);
    const meetsMinQty = currentTotalQty >= bundleMinQty;
    const meetsMinAmount = bundleMinAmount === 0 || currentItemTotal >= bundleMinAmount;
    
    // Check if all bundle entries can be satisfied with equal/better options
    let allEntriesSatisfied = true;
    
    for (const bundleEntry of selectedBundle.optionBundleEntries) {
        let entrySatisfied = false;
        
        for (const item of order.fundraiserOrderItems) {
            for (const option of item.fundraiserOrderItemOptions) {
                if (option.qty >= bundleEntry.quantity) {
                    // Find original bundle option price
                    const originalOption = order.fundraiserOrderItems
                        .flatMap(i => i.fundraiserOrderItemOptions)
                        .find(o => o.itemOptionId === bundleEntry.itemsOptionId);
                    
                    // Satisfied if same option or equal/higher price
                    if (option.itemOptionId === bundleEntry.itemsOptionId || 
                        (originalOption && option.price >= originalOption.price)) {
                        entrySatisfied = true;
                        break;
                    }
                }
            }
            if (entrySatisfied) break;
        }
        
        if (!entrySatisfied) {
            allEntriesSatisfied = false;
            break;
        }
    }
    
    const isComplete = meetsMinQty && meetsMinAmount && allEntriesSatisfied;
    return isComplete ? 'complete' : 'incomplete';
};
